import { FormGroup } from '@angular/forms';
import { ContentAssetRule, DataMartField, lookupComparators, RuleCriterionData } from '@r1-compass-library/models';
import { ContentLibraryRuleForm } from '@r1-compass-library/models/content-library/content-library-rule-form.model';
import { GenericSelectionCriterionForm } from '@r1-compass-library/models/criteria/selection-criteria-form.model';
// eslint-disable-next-line no-restricted-imports
import { PlayBuilderForm } from '../../../../../r1-compass-application/src/app/components/plays/play-record-selection/play-record-play-builder/play-builder-form.model';
import {
    isGenericForm,
    TemplateCriteriaBuilderForm,
} from '@r1-compass-library/models/template-criteria/template-criteria-form.model';

export class DisableValue1AndValue2Conditionally {
    /**
     * @description Check the generic criteria.
     * - If comparator is "isnull" or "blank" (aka type "nothing"), disable value control.
     */
    static disableContentLibrary(
        ruleData: ContentAssetRule,
        contentLibraryRuleForm: FormGroup<ContentLibraryRuleForm>
    ) {
        const dataMartField = ruleData.field;
        const comparator = contentLibraryRuleForm.controls.comparator.value;
        if (dataMartField) {
            const comparatorType = dataMartField
                ? lookupComparators(dataMartField).find(comparatorMetadata => comparatorMetadata.value === comparator)
                      ?.valueType
                : undefined;

            if (comparatorType === 'nothing') {
                contentLibraryRuleForm.controls.value.disable();
            }
        }
    }

    /**
     * @description Check the generic criteria.
     * - Begin with assuming only val 1 is required (disable val2)
     * - If comparator is "isnull" or "blank" (aka type "nothing"), disable both val controls.
     * - If comparator is type that requires 2 vals, enable val2.
     */
    static disableLifecycleRules(
        ruleData: RuleCriterionData,
        selectionCriterionForm: FormGroup<GenericSelectionCriterionForm>
    ) {
        selectionCriterionForm.controls.value2.disable();

        const dataMartField = ruleData.dataMartField;
        const comparator = selectionCriterionForm.controls.comparator.value;
        if (dataMartField) {
            const comparatorType = dataMartField
                ? lookupComparators(dataMartField).find(comparatorMetadata => comparatorMetadata.value === comparator)
                      ?.valueType
                : undefined;

            if (comparatorType === 'nothing') {
                selectionCriterionForm.controls.value1.disable();
                selectionCriterionForm.controls.value2.disable();
            } else if (
                comparatorType === 'date&date' ||
                comparatorType === 'text&text' ||
                comparatorType === 'timeInterval'
            ) {
                selectionCriterionForm.controls.value2.enable();
            }
        }
    }

    /**
     * @description Check the generic criteria.
     * - Begin with assuming only val 1 is required (disable val2)
     * - If comparator is "isnull" or "blank" (aka type "nothing"), disable both val controls.
     * - If comparator is type that requires 2 vals, enable val2.
     */
    static disablePlays(builderForm: FormGroup<PlayBuilderForm>, usedDataMartFields: DataMartField[] | undefined) {
        validateTemplateCriteriaTypeForms(builderForm, usedDataMartFields);
    }

    /**
     * @description Check the generic criteria.
     * - Begin with assuming only val 1 is required (disable val2)
     * - If comparator is "isnull" or "blank" (aka type "nothing"), disable both val controls.
     * - If comparator is type that requires 2 vals, enable val2.
     */
    static disableTemplateCriteria(
        builderForm: FormGroup<TemplateCriteriaBuilderForm>,
        usedDataMartFields: DataMartField[] | undefined
    ) {
        validateTemplateCriteriaTypeForms(builderForm, usedDataMartFields);
    }
}

/**
 * @description Check the generic criteria.
 * - Begin with assuming only val 1 is required (disable val2)
 * - If comparator is "isnull" or "blank" (aka type "nothing"), disable both val controls.
 * - If comparator is type that requires 2 vals, enable val2.
 */
function validateTemplateCriteriaTypeForms(
    form: FormGroup<TemplateCriteriaBuilderForm> | FormGroup<PlayBuilderForm>,
    usedDataMartFields: DataMartField[] | undefined
) {
    form.controls.selectionCriteria.controls.forEach(c => {
        if (isGenericForm(c.controls.data)) {
            const typedForm = c.controls.data;
            typedForm.controls.value2.disable();

            const dataMartFieldName = typedForm.controls.dataMartFieldName.value;
            const dataMartField = usedDataMartFields?.find(field => field.name === dataMartFieldName);
            const comparator = typedForm.controls.comparator.value;
            const comparatorType = dataMartField
                ? lookupComparators(dataMartField).find(comparatorMetadata => comparatorMetadata.value === comparator)
                      ?.valueType
                : undefined;

            if (comparatorType === 'nothing') {
                typedForm.controls.value1.disable();
                typedForm.controls.value2.disable();
            } else if (
                comparatorType === 'date&date' ||
                comparatorType === 'text&text' ||
                comparatorType === 'timeInterval'
            ) {
                typedForm.controls.value2.enable();
            }
        }
    });
}
